import { Section } from "@jfrk/react-heading-levels";
import ModuleWrapper from "@municipio/gatsby-theme-basic/src/components/ModuleWrapper";
import { apartmentContext } from "@whitespace/gatsby-plugin-smorgasbord-fastapi";
import FastAPIContext from "@whitespace/gatsby-plugin-smorgasbord-fastapi/src/contexts/FastAPIContext";
import { Loading } from "@whitespace/gatsby-theme-smorgasbord/src/components";
import { HTML } from "@whitespace/gatsby-theme-wordpress-basic/src/components";
import clsx from "clsx";
import {
  addDays,
  addMonths,
  addYears,
  endOfDay,
  endOfMonth,
  endOfYear,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import PropTypes from "prop-types";
import React, { useContext, useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
// import { useTranslation } from "react-i18next";

import * as defaultStyles from "./SmorgasbordConsumptionHistoryModule.module.css";

SmorgasbordConsumptionHistoryModule.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  title: PropTypes.node,
  styles: PropTypes.objectOf(PropTypes.string),
};

const resolutions = {
  hour: {
    dateFormat: {
      minute: "2-digit",
      hour: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    },
    chartDateFormat: { hour: "numeric" },
  },
  day: {
    dateFormat: {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    },
    chartDateFormat: { day: "numeric" },
  },
  month: {
    dateFormat: {
      month: "long",
      year: "numeric",
    },
    chartDateFormat: { month: "short" },
  },
};

const viewModes = {
  day: {
    label: "Dygn",
    resolution: "hour",
    getRange: (date) => ({ from: startOfDay(date), to: endOfDay(date) }),
    step: { add: addDays, sub: subDays },
    dateFormat: {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  },
  month: {
    label: "Månad",
    resolution: "day",
    getRange: (date) => ({ from: startOfMonth(date), to: endOfMonth(date) }),
    step: { add: addMonths, sub: subMonths },
    dateFormat: { month: "long", year: "numeric" },
  },
  year: {
    label: "År",
    resolution: "month",
    getRange: (date) => ({ from: startOfYear(date), to: endOfYear(date) }),
    step: { add: addYears, sub: subYears },
    dateFormat: { year: "numeric" },
  },
};

const stockholmPartsFormatter = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Stockholm",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
});

const getStockholmPartsFromUnix = (unixTimeSeconds) => {
  const parts = stockholmPartsFormatter.formatToParts(unixTimeSeconds * 1000);
  return {
    year: Number(parts.find((part) => part.type === "year")?.value),
    month: Number(parts.find((part) => part.type === "month")?.value),
    day: Number(parts.find((part) => part.type === "day")?.value),
    hour: Number(parts.find((part) => part.type === "hour")?.value),
  };
};

const getStockholmPartsFromDate = (date) =>
  getStockholmPartsFromUnix(date.getTime() / 1000);

const capitalize = (value) =>
  value ? value.replace(/^./, (str) => str.toUpperCase()) : value;

const formatDateLabel = (date, options) =>
  capitalize(
    date.toLocaleDateString("sv-SE", {
      timeZone: "Europe/Stockholm",
      ...options,
    }),
  );

const formatVolume = (value) =>
  value.toLocaleString("sv-SE", { maximumFractionDigits: 0 });

const formatCompactDateLabel = (date, options) =>
  date.toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
    ...options,
  });

const formatCompactTimeLabel = (date, options) =>
  date.toLocaleTimeString("sv-SE", {
    timeZone: "Europe/Stockholm",
    ...options,
  });

const formatTooltipDate = (date, resolution) => {
  if (resolution === "hour") {
    return `${formatCompactDateLabel(date, {
      day: "numeric",
      month: "numeric",
    })} kl. ${formatCompactTimeLabel(date, {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }

  if (resolution === "day") {
    return formatCompactDateLabel(date, {
      day: "numeric",
      month: "numeric",
    });
  }

  return formatCompactDateLabel(date, {
    month: "short",
    year: "numeric",
  });
};

const formatTouchDetailsTitle = (viewMode, date) => {
  if (viewMode === "month") {
    return `Vald dag: ${formatDateLabel(date, {
      day: "numeric",
      month: "long",
    })}`;
  }

  return `Vald månad: ${formatDateLabel(date, {
    month: "long",
    year: "numeric",
  })}`;
};

const formatTouchDetailsPeriodLabel = (date) =>
  formatDateLabel(date, {
    month: "long",
    year: "numeric",
  });

export default function SmorgasbordConsumptionHistoryModule({
  styles = defaultStyles,
  title,
  description,
  ...restProps
}) {
  // const { t } = useTranslation();
  const { pending } = useContext(FastAPIContext);
  const { selectedApartment } = useContext(apartmentContext);

  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState("day");
  const [refDate, setRefDate] = useState(subDays(new Date(), 1));
  const [busy, setBusy] = useState(false);
  const [resolvedResolution, setResolvedResolution] = useState("hour");
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDraftValue, setPickerDraftValue] = useState("");
  const [touchDrilldownBucket, setTouchDrilldownBucket] = useState(null);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const pickerPopoverRef = useRef(null);
  const pickerInputRef = useRef(null);

  const normalizeDateForViewMode = (mode, date) => {
    if (mode === "year") return startOfYear(date);
    if (mode === "month") return startOfMonth(date);
    return startOfDay(date);
  };

  const formatInputValue = (mode, date) => {
    const pad = (value) => String(value).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());

    if (mode === "year") return `${year}`;
    if (mode === "month") return `${year}-${month}`;
    return `${year}-${month}-${day}`;
  };

  const parseInputValue = (mode, value) => {
    if (!value) return null;
    if (mode === "year") {
      const year = Number(value);
      if (!year) return null;
      return startOfYear(new Date(year, 0, 1));
    }
    if (mode === "month") {
      const [year, month] = value.split("-").map(Number);
      if (!year || !month) return null;
      return startOfMonth(new Date(year, month - 1, 1));
    }
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return startOfDay(new Date(year, month - 1, day));
  };

  const fetchData = async (mode, date, compare) => {
    if (!selectedApartment) return;
    setBusy(true);
    try {
      const { from, to } = mode.getRange(date);
      const buildRequest = (range) => {
        const qs = new URLSearchParams();
        qs.set("resolution", mode.resolution);
        qs.set("from", range.from.toISOString());
        qs.set("to", range.to.toISOString());
        return window.fetch(
          `/api/elvaco/apartments/${
            selectedApartment.id
          }/meters/measurements?${qs.toString()}`,
        );
      };

      const requests = [buildRequest({ from, to })];
      if (compare) {
        const prevDate = mode.step.sub(date, 1);
        const prevRange = mode.getRange(prevDate);
        requests.push(buildRequest(prevRange));
      }

      const responses = await Promise.all(requests);
      const json = await Promise.all(responses.map((res) => res.json()));
      const currentData = json[0]?.data ?? [];
      const previousData = compare ? json[1]?.data ?? [] : null;

      setResolvedResolution(mode.resolution);
      setData({ current: currentData, previous: previousData });
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const mode = viewModes[viewMode];
    fetchData(mode, refDate, compareEnabled);
  }, [selectedApartment, viewMode, refDate, compareEnabled]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const updatePointerType = (event) => {
      setIsCoarsePointer(event.matches);
    };

    setIsCoarsePointer(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updatePointerType);
      return () => mediaQuery.removeEventListener("change", updatePointerType);
    }

    mediaQuery.addListener(updatePointerType);
    return () => mediaQuery.removeListener(updatePointerType);
  }, []);

  useEffect(() => {
    setTouchDrilldownBucket(null);
  }, [viewMode, refDate, compareEnabled, data]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!pickerPopoverRef.current?.contains(event.target)) {
        setPickerOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPickerOpen(false);
      }
      if (event.key === "Enter" && viewMode === "year") {
        const nextDate = parseInputValue(viewMode, pickerDraftValue);
        if (nextDate) {
          setRefDate(nextDate);
          setPickerOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pickerDraftValue, pickerOpen, viewMode]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    setPickerDraftValue(formatInputValue(viewMode, refDate));

    const timeoutId = window.setTimeout(() => {
      if (!pickerInputRef.current) return;
      pickerInputRef.current.focus();
      if (pickerInputRef.current.showPicker) {
        pickerInputRef.current.showPicker();
      } else if (pickerInputRef.current.select) {
        pickerInputRef.current.select();
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pickerOpen, refDate, viewMode]);

  const handleViewModeChange = (nextMode) => {
    setViewMode(nextMode);
    setPickerOpen(false);
  };

  const handleStep = (direction) => {
    const mode = viewModes[viewMode];
    const fn = direction === "next" ? mode.step.add : mode.step.sub;
    setRefDate((d) => fn(d, 1));
  };

  const openPicker = () => {
    setPickerDraftValue(formatInputValue(viewMode, refDate));
    setPickerOpen(true);
  };

  const applyPickerValue = (value) => {
    const nextDate = parseInputValue(viewMode, value);
    if (!nextDate) return;
    setRefDate(normalizeDateForViewMode(viewMode, nextDate));
    setPickerOpen(false);
  };

  const drillDownToBucket = (bucket) => {
    if (bucket == null) return;
    if (viewMode === "year") {
      setViewMode("month");
      setRefDate(startOfMonth(new Date(refDate.getFullYear(), bucket - 1, 1)));
    } else if (viewMode === "month") {
      setViewMode("day");
      setRefDate(
        startOfDay(new Date(refDate.getFullYear(), refDate.getMonth(), bucket)),
      );
    }
  };

  const handleBucketClick = (bucket) => {
    if (bucket == null || viewMode === "day") return;
    if (!isCoarsePointer) {
      drillDownToBucket(bucket);
      return;
    }

    setTouchDrilldownBucket((currentBucket) => {
      if (currentBucket === bucket) {
        drillDownToBucket(bucket);
        return null;
      }
      return bucket;
    });
  };

  const isInPeriod = (unixTimeSeconds, periodDate, resolution) => {
    const periodParts = getStockholmPartsFromDate(periodDate);
    const parts = getStockholmPartsFromUnix(unixTimeSeconds);
    if (resolution === "hour") {
      return (
        parts.year === periodParts.year &&
        parts.month === periodParts.month &&
        parts.day === periodParts.day
      );
    }
    if (resolution === "day") {
      return (
        parts.year === periodParts.year && parts.month === periodParts.month
      );
    }
    return parts.year === periodParts.year;
  };

  const singleValues = React.useMemo(() => {
    if (!data?.current || data.current.length === 0) return [];
    const values = data.current[0].values
      .filter(({ value }, index, arr) => {
        if (value != null) return true;
        const hasNonNullBefore = arr
          .slice(0, index)
          .some((item) => item.value != null);
        const hasNonNullAfter = arr
          .slice(index + 1)
          .some((item) => item.value != null);
        return hasNonNullBefore && hasNonNullAfter;
      })
      .filter(({ when }) =>
        isInPeriod(when, refDate, viewModes[viewMode].resolution),
      );

    const bucketMap = new Map();
    values.forEach((entry) => {
      const parts = getStockholmPartsFromUnix(entry.when);
      const bucket =
        resolvedResolution === "hour"
          ? parts.hour
          : resolvedResolution === "day"
          ? parts.day
          : parts.month;
      bucketMap.set(bucket, entry.value);
    });

    let minBucket = 1;
    let maxBucket = 12;
    if (resolvedResolution === "hour") {
      minBucket = 0;
      maxBucket = 23;
    } else if (resolvedResolution === "day") {
      minBucket = 1;
      maxBucket = getDaysInMonth(refDate);
    }

    return Array.from({ length: maxBucket - minBucket + 1 }, (_, index) => {
      const bucket = minBucket + index;
      return { bucket, value: bucketMap.get(bucket) ?? null };
    });
  }, [data, refDate, resolvedResolution, viewMode]);

  const comparedValues = React.useMemo(() => {
    if (!compareEnabled) return null;
    if (!data?.current || data.current.length === 0) return [];

    const getBucket = (unixTime) => {
      const parts = getStockholmPartsFromUnix(unixTime);
      if (resolvedResolution === "hour") return parts.hour;
      if (resolvedResolution === "day") return parts.day;
      return parts.month;
    };

    const normalizeValues = (values, periodDate) =>
      values
        .filter(({ value }, index, arr) => {
          if (value != null) return true;
          const hasNonNullBefore = arr
            .slice(0, index)
            .some((item) => item.value != null);
          const hasNonNullAfter = arr
            .slice(index + 1)
            .some((item) => item.value != null);
          return hasNonNullBefore && hasNonNullAfter;
        })
        .filter(({ when }) => isInPeriod(when, periodDate, resolvedResolution))
        .slice()
        .sort((a, b) => a.when - b.when);

    const previousRefDate = viewModes[viewMode].step.sub(refDate, 1);

    const currentValues = normalizeValues(
      data.current[0]?.values ?? [],
      refDate,
    );
    const previousValues = normalizeValues(
      data.previous?.[0]?.values ?? [],
      previousRefDate,
    );

    const currentMap = new Map();
    const previousMap = new Map();
    currentValues.forEach((entry) => {
      currentMap.set(getBucket(entry.when), entry.value);
    });
    previousValues.forEach((entry) => {
      previousMap.set(getBucket(entry.when), entry.value);
    });

    let minBucket = 1;
    let maxBucket = 12;
    if (resolvedResolution === "hour") {
      minBucket = 0;
      maxBucket = 23;
    } else if (resolvedResolution === "day") {
      minBucket = 1;
      maxBucket = getDaysInMonth(refDate);
    }

    const buckets = Array.from(
      { length: maxBucket - minBucket + 1 },
      (_, index) => minBucket + index,
    );

    return buckets.map((bucket) => ({
      bucket,
      currentValue: currentMap.get(bucket) ?? null,
      previousValue: previousMap.get(bucket) ?? null,
    }));
  }, [compareEnabled, data, resolvedResolution, refDate, viewMode]);

  const chartData = compareEnabled ? comparedValues ?? [] : singleValues;
  const hasChartData = compareEnabled
    ? chartData.some(
        ({ currentValue, previousValue }) =>
          currentValue != null || previousValue != null,
      )
    : chartData.some(({ value }) => value != null);
  const showNoDataMessage = data != null && !busy && !hasChartData;

  const formatBucketLabel = (bucket) => {
    let labelDate = null;
    if (resolvedResolution === "hour") {
      labelDate = new Date(
        refDate.getFullYear(),
        refDate.getMonth(),
        refDate.getDate(),
        bucket,
        0,
        0,
      );
    } else if (resolvedResolution === "day") {
      labelDate = new Date(refDate.getFullYear(), refDate.getMonth(), bucket);
    } else {
      labelDate = new Date(refDate.getFullYear(), bucket - 1, 1);
    }

    const formatter =
      resolvedResolution === "hour"
        ? labelDate.toLocaleString
        : labelDate.toLocaleDateString;

    return formatter.call(labelDate, "sv-SE", {
      timeZone: "Europe/Stockholm",
      ...resolutions[resolvedResolution].chartDateFormat,
    });
  };

  const getBucketDate = (baseDate, bucket) => {
    if (resolvedResolution === "hour") {
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        bucket,
        0,
        0,
      );
    }
    if (resolvedResolution === "day") {
      return new Date(baseDate.getFullYear(), baseDate.getMonth(), bucket);
    }
    return new Date(baseDate.getFullYear(), bucket - 1, 1);
  };

  const formatTooltipValue = (date, value) =>
    `${formatTooltipDate(date, resolvedResolution)}: ${formatVolume(
      value,
    )} liter`;

  const renderCompareTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const currentEntry = payload.find(
      (item) => item.dataKey === "currentValue",
    );
    const previousEntry = payload.find(
      (item) => item.dataKey === "previousValue",
    );
    const previousRefDate = viewModes[viewMode].step.sub(refDate, 1);
    const currentDate = getBucketDate(refDate, label);
    const previousDate = getBucketDate(previousRefDate, label);

    const hasCurrentValue = currentEntry?.value != null;
    const hasPreviousValue = previousEntry?.value != null;

    if (!hasCurrentValue && !hasPreviousValue) return null;

    return (
      <div className={clsx("recharts-default-tooltip", styles.tooltip)}>
        {hasCurrentValue && (
          <div className={styles.tooltipRow}>
            {formatTooltipValue(currentDate, currentEntry.value)}
          </div>
        )}
        {hasPreviousValue && (
          <div className={styles.tooltipRow}>
            {formatTooltipValue(previousDate, previousEntry.value)}
          </div>
        )}
      </div>
    );
  };

  const renderSingleTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const entry = payload.find((item) => item.dataKey === "value");
    if (!entry || entry.value == null) return null;
    const currentDate = getBucketDate(refDate, label);
    return (
      <div className={clsx("recharts-default-tooltip", styles.tooltip)}>
        <div className={styles.tooltipRow}>
          {formatTooltipValue(currentDate, entry.value)}
        </div>
      </div>
    );
  };

  const formatPeriodLabel = (mode, date) =>
    formatDateLabel(date, viewModes[mode].dateFormat);

  const compareLegendItems = React.useMemo(() => {
    if (!compareEnabled) return [];
    const previousRefDate = viewModes[viewMode].step.sub(refDate, 1);
    return [
      {
        color: "#297bb3",
        label: formatPeriodLabel(viewMode, refDate),
      },
      {
        color: "#9db2c7",
        label: formatPeriodLabel(viewMode, previousRefDate),
      },
    ];
  }, [compareEnabled, refDate, viewMode]);

  const touchDetails = React.useMemo(() => {
    if (
      !isCoarsePointer ||
      viewMode === "day" ||
      touchDrilldownBucket == null
    ) {
      return null;
    }

    const currentDate = getBucketDate(refDate, touchDrilldownBucket);
    const previousRefDate = viewModes[viewMode].step.sub(refDate, 1);
    const previousDate = getBucketDate(previousRefDate, touchDrilldownBucket);

    if (compareEnabled) {
      const selectedEntry = comparedValues?.find(
        (entry) => entry.bucket === touchDrilldownBucket,
      );

      if (!selectedEntry) return null;

      return {
        title: formatTouchDetailsTitle(viewMode, currentDate),
        items: [
          selectedEntry.currentValue != null && {
            color: "#297bb3",
            label: `${formatTouchDetailsPeriodLabel(
              currentDate,
            )}: ${formatVolume(selectedEntry.currentValue)} liter`,
          },
          selectedEntry.previousValue != null && {
            color: "#9db2c7",
            label: `${formatTouchDetailsPeriodLabel(
              previousDate,
            )}: ${formatVolume(selectedEntry.previousValue)} liter`,
          },
        ].filter(Boolean),
      };
    }

    const selectedEntry = singleValues.find(
      (entry) => entry.bucket === touchDrilldownBucket,
    );

    if (!selectedEntry) return null;

    return {
      title: formatTouchDetailsTitle(viewMode, currentDate),
      items: [
        {
          color: "#297bb3",
          label:
            selectedEntry.value == null
              ? "Ingen data"
              : `${formatVolume(selectedEntry.value)} liter`,
        },
      ],
    };
  }, [
    compareEnabled,
    comparedValues,
    isCoarsePointer,
    refDate,
    singleValues,
    touchDrilldownBucket,
    viewMode,
  ]);

  // Keep the touch details panel ready, but hide it until the customer asks for
  // more than chart-level selection feedback.
  const showTouchDetails = false;

  const pickerInputType =
    viewMode === "year" ? "number" : viewMode === "month" ? "month" : "date";

  if (pending) {
    return <Loading />;
  }

  return (
    <ModuleWrapper
      title={title && <div className={clsx(styles.title, "h1")}>{title}</div>}
      {...restProps}
      className={styles.title}
    >
      <Section>
        {description && <HTML>{description}</HTML>}
        <div className={styles.controls}>
          <div className={styles.primaryControls}>
            <div className={styles.viewModeData}>
              <label htmlFor="viewMode" className={styles.srOnly}>
                Visa
              </label>
              <div className={styles.viewModeButtons}>
                {Object.entries(viewModes).map(([modeKey, mode]) => (
                  <button
                    key={modeKey}
                    type="button"
                    onClick={() => {
                      handleViewModeChange(modeKey);
                    }}
                    className={clsx(
                      styles.viewModeButton,
                      viewMode === modeKey && styles.viewModeButtonActive,
                    )}
                    disabled={busy}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.viewModeData}>
              <div className={styles.viewModeButtons}>
                <button
                  type="button"
                  onClick={() => setCompareEnabled((prev) => !prev)}
                  className={clsx(
                    styles.viewModeButton,
                    styles.compareButton,
                    compareEnabled && styles.viewModeButtonActive,
                  )}
                  disabled={busy}
                >
                  <span
                    className={styles.compareCheckbox}
                    data-checked={compareEnabled ? "true" : "false"}
                    aria-hidden="true"
                  />
                  Jämför med tidigare period
                </button>
              </div>
            </div>
          </div>

          <div className={styles.navigation}>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                handleStep("prev");
              }}
              disabled={busy}
            >
              &larr; <span className={styles.navLabel}>Föregående</span>
            </button>
            <span className={styles.currentLabel}>
              <span className={styles.datePickerWrapper}>
                <button
                  type="button"
                  className={styles.dateButton}
                  onClick={() => {
                    if (pickerOpen) {
                      setPickerOpen(false);
                    } else {
                      openPicker();
                    }
                  }}
                  aria-label="Välj period"
                  aria-expanded={pickerOpen}
                >
                  <span>{formatPeriodLabel(viewMode, refDate)}</span>
                  <span aria-hidden="true" className={styles.dateChevron}>
                    ▾
                  </span>
                </button>
                {pickerOpen && (
                  <span
                    className={styles.datePickerPopover}
                    ref={pickerPopoverRef}
                  >
                    <label htmlFor="periodPicker" className={styles.srOnly}>
                      Välj period
                    </label>
                    <span className={styles.datePickerFields}>
                      <input
                        ref={pickerInputRef}
                        id="periodPicker"
                        className={styles.periodInput}
                        type={pickerInputType}
                        inputMode={viewMode === "year" ? "numeric" : undefined}
                        min={viewMode === "year" ? "2000" : undefined}
                        max={
                          viewMode === "year"
                            ? `${new Date().getFullYear() + 1}`
                            : undefined
                        }
                        value={pickerDraftValue}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setPickerDraftValue(nextValue);
                          if (viewMode !== "year") {
                            applyPickerValue(nextValue);
                          }
                        }}
                        disabled={busy}
                      />
                      {viewMode === "year" && (
                        <button
                          type="button"
                          className={styles.pickerConfirmButton}
                          onClick={() => applyPickerValue(pickerDraftValue)}
                          disabled={busy}
                        >
                          Visa
                        </button>
                      )}
                    </span>
                  </span>
                )}
              </span>
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                handleStep("next");
              }}
              disabled={busy}
            >
              <span className={styles.navLabel}>Nästa</span> &rarr;
            </button>
          </div>

          {busy && (
            <span aria-live="polite" className={styles.busyMessage}>
              {" "}
              Hämtar data…
            </span>
          )}
        </div>

        <div className={clsx(busy && styles.busy, styles.graphContainer)}>
          {compareEnabled && compareLegendItems.length > 0 && (
            <div
              className={styles.compareLegend}
              aria-label="Jämförda perioder"
            >
              {compareLegendItems.map((item) => (
                <div className={styles.compareLegendItem} key={item.label}>
                  <span
                    className={styles.compareLegendSwatch}
                    style={{ backgroundColor: item.color }}
                    aria-hidden="true"
                  />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.graphInner}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                barCategoryGap={compareEnabled ? "8%" : "4%"}
                barGap={compareEnabled ? 0 : 2}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  tick={{ fontSize: 14 }}
                  tickFormatter={(value) => formatBucketLabel(value)}
                />
                <YAxis tick={{ fontSize: 14 }} width={40} />
                <Tooltip
                  content={
                    compareEnabled ? renderCompareTooltip : renderSingleTooltip
                  }
                />
                {compareEnabled ? (
                  <>
                    <Bar
                      dataKey="currentValue"
                      fill="#297bb3"
                      name="Vald period"
                    >
                      {chartData.map((entry) => (
                        <Cell
                          key={`current-${entry.bucket}`}
                          fillOpacity={
                            touchDrilldownBucket == null ||
                            touchDrilldownBucket === entry.bucket
                              ? 1
                              : 0.45
                          }
                        />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="previousValue"
                      fill="#9db2c7"
                      name="Föregående period"
                    >
                      {chartData.map((entry) => (
                        <Cell
                          key={`previous-${entry.bucket}`}
                          fillOpacity={
                            touchDrilldownBucket == null ||
                            touchDrilldownBucket === entry.bucket
                              ? 1
                              : 0.45
                          }
                        />
                      ))}
                    </Bar>
                  </>
                ) : (
                  <Bar
                    dataKey="value"
                    fill="#297bb3"
                    name="Volym"
                    cursor={viewMode === "day" ? "default" : "pointer"}
                    onClick={(barData) =>
                      handleBucketClick(barData?.payload?.bucket)
                    }
                  >
                    {chartData.map((entry) => (
                      <Cell
                        key={`single-${entry.bucket}`}
                        fillOpacity={
                          touchDrilldownBucket == null ||
                          touchDrilldownBucket === entry.bucket
                            ? 1
                            : 0.45
                        }
                      />
                    ))}
                  </Bar>
                )}
                {viewMode !== "day" &&
                  chartData.map((entry, index) => (
                    <ReferenceArea
                      key={`${entry.bucket}-${index}`}
                      x1={entry.bucket}
                      x2={entry.bucket}
                      fill="transparent"
                      ifOverflow="extendDomain"
                      className={styles.bucketClickArea}
                      onClick={() => handleBucketClick(entry.bucket)}
                    />
                  ))}
              </BarChart>
            </ResponsiveContainer>
            {showNoDataMessage && (
              <div className={styles.noDataOverlay}>Ingen data ännu</div>
            )}
          </div>

          {showTouchDetails &&
            touchDetails &&
            touchDetails.items.length > 0 && (
              <div className={styles.touchDetails}>
                <div className={styles.touchDetailsTitle}>
                  {touchDetails.title}
                </div>
                <div className={styles.touchDetailsHint}>
                  Tryck igen för detaljerad vy.
                </div>
                {touchDetails.items.map((item) => (
                  <div className={styles.touchDetailsRow} key={item.label}>
                    <span
                      className={styles.compareLegendSwatch}
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      </Section>
    </ModuleWrapper>
  );
}
